import { BACKEND_URL } from '../../../utils/config';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      // Try to get token from Redux state first
      const state = getState();
      let token;
      
      if (state.auth && state.auth.token) {
        token = state.auth.token;
      } else {
        // Fallback to localStorage
        token = localStorage.getItem('token');
      }
      
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['StripeAccount', 'AccountStatus', 'AccountBalance', 'Orders', 'PlatformPendingProductEarnings', 'SavedPaymentMethods', 'MerchantFundCollectionPayments'],
  endpoints: (builder) => ({
    // Stripe Connect endpoints
    createStripeAccount: builder.mutation({
      query: (data = {}) => ({
        url: '/stripe/create-account',
        method: 'POST',
        body: data // Pass any data including country
      }),
      invalidatesTags: ['StripeAccount'],
      transformResponse: (response) => {
        console.log('Create Stripe account response:', response);
        return response;
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
          console.log('Stripe account created:', result.data);
        } catch (error) {
          console.error('Error creating Stripe account:', error);
        }
      },
    }),
    createAccountSession: builder.mutation({
      query: (data) => {
        // Handle both string accountId or object with parameters
        const isString = typeof data === 'string';
        
        return {
          url: '/stripe/account-session',
          method: 'POST',
          body: isString 
            ? { account: data } // Old format: just the account ID
            : data // New format: object with account, available_countries, etc.
        };
      },
      transformResponse: (response) => {
        console.log('Create account session response:', response);
        return response;
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
          console.log('Account session created:', result.data);
        } catch (error) {
          console.error('Error creating account session:', error);
        }
      },
    }),
    getAccountStatus: builder.query({
      query: () => '/stripe/account-status',
      providesTags: ['AccountStatus'],
      transformResponse: (response) => {
        console.log('Get account status response:', response);
        if (!response || !response.success) {
          console.warn('Received unexpected response format:', response);
          return {
            status: {
              detailsSubmitted: false,
              chargesEnabled: false,
            },
            error: 'Unexpected response format'
          };
        }
        return response;
      },
      transformErrorResponse: (response, meta, arg) => {
        console.error('Error retrieving account status:', response);
        
        // Extract the specific error message if available
        let errorMessage = 'Failed to retrieve account status';
        try {
          if (response.data && response.data.error) {
            errorMessage = response.data.error;
          } else if (typeof response.error === 'string') {
            errorMessage = response.error;
          } else if (response.status === 404) {
            errorMessage = 'Endpoint not found (404). Check API configuration.';
          } else if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          }
        } catch (err) {
          console.error('Error parsing error response:', err);
        }
        
        // Return a default status object for UI handling
        return {
          status: {
            detailsSubmitted: false,
            chargesEnabled: false
          },
          error: errorMessage
        };
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
          console.log('Account status retrieved:', result.data);
        } catch (error) {
          console.error('Error getting account status:', error);
          // Errors are handled by transformErrorResponse
        }
      },
    }),
    getAccountBalance: builder.query({
      query: () => '/stripe/account-balance',
      providesTags: ['AccountBalance'],
      transformResponse: (response) => {
        console.log('Get account balance response:', response);
        if (!response || !response.success) {
          console.warn('Received unexpected balance response format:', response);
          return {
            balance: {
              available: [],
              pending: []
            },
            transactions: [],
            error: 'Unexpected response format'
          };
        }
        return response;
      },
      transformErrorResponse: (response, meta, arg) => {
        console.error('Error retrieving account balance:', response);
        
        // Extract the specific error message if available
        let errorMessage = 'Failed to retrieve account balance';
        try {
          if (response.data && response.data.error) {
            errorMessage = response.data.error;
          } else if (typeof response.error === 'string') {
            errorMessage = response.error;
          } else if (response.status === 404) {
            errorMessage = 'Balance endpoint not found (404). Check API configuration.';
          } else if (response.status === 401) {
            errorMessage = 'Authentication required. Please log in again.';
          }
        } catch (err) {
          console.error('Error parsing balance error response:', err);
        }
        
        // Return a default balance object for UI handling
        return {
          balance: {
            available: [],
            pending: []
          },
          transactions: [],
          error: errorMessage
        };
      },
      onQueryStarted: async (_, { queryFulfilled }) => {
        try {
          const result = await queryFulfilled;
          console.log('Account balance retrieved:', result.data);
        } catch (error) {
          console.error('Error getting account balance:', error);
          // Errors are handled by transformErrorResponse
        }
      },
    }),

    // Payment processing endpoints
    createPaymentIntent: builder.mutation({
      query: (data) => ({
        url: '/payments/create-payment-intent',
        method: 'POST',
        body: {
          amount: data.amount,
          currency: data.currency || 'usd',
          transfer_group: data.orderId ? `ORDER_${data.orderId}` : undefined,
          metadata: data.metadata || {}
        },
      }),
    }),
    confirmPayment: builder.mutation({
      query: ({ orderId, paymentIntentId }) => ({
        url: `/orders/${orderId}`,
        method: 'PUT',
        body: {
          paymentStatus: 'paid',
          stripePaymentIntentId: paymentIntentId,
        },
      }),
    }),
    fetchPaymentMethods: builder.query({
      query: (userId) => `/users/${userId}/payment-methods`,
    }),
    refundPayment: builder.mutation({
      query: (data) => ({
        url: '/payments/refund',
        method: 'POST',
        body: data,
      }),
    }),

    // New endpoints for multi-merchant payments and transfers
    createMultiMerchantPaymentIntent: builder.mutation({
      query: (data) => ({
        url: '/payment/create-multi-merchant-payment-intent',
        method: 'POST',
        body: {
          cartItems: data.cartItems,
          currency: data.currency || 'usd',
          orderId: data.orderId,
          shippingCost: data.shippingCost
        },
      }),
    }),
    
    // Get payments for specific order
    getPaymentsByOrderId: builder.query({
      query: (orderId) => `/payment/order/${orderId}`,
    }),
    
    // Order status update (which will trigger automatic transfers when completed)
    updateOrderStatus: builder.mutation({
      query: ({ orderId, status }) => ({
        url: `/orders/${orderId}/status`,
        method: 'PUT',
        body: { status }
      }),
      invalidatesTags: ['Orders'],
    }),

    getPlatformPendingProductEarnings: builder.query({
      query: () => '/payment/me/platform-pending-product-earnings',
      providesTags: (result, error, arg) =>
        result
          ? [{ type: 'PlatformPendingProductEarnings', id: 'LIST' }]
          : [{ type: 'PlatformPendingProductEarnings', id: 'LIST' }],
      transformResponse: (response) => {
        // console.log('Get Platform Pending Product Earnings response:', response);
        return response;
      },
    }),

    // Add these endpoints to the paymentApi.endpoints object
    getSavedPaymentMethods: builder.query({
      query: () => ({
        url: `/payment-methods`,
        method: 'GET',
      }),
      transformResponse: (response) => response,
      providesTags: ['SavedPaymentMethods'],
    }),

    createSetupIntent: builder.mutation({
      query: () => ({
        url: `/payment-methods/setup-intent`,
        method: 'POST',
      }),
    }),

    savePaymentMethod: builder.mutation({
      query: (data) => ({
        url: `/payment-methods`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['SavedPaymentMethods'],
    }),

    deletePaymentMethod: builder.mutation({
      query: (id) => ({
        url: `/payment-methods/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SavedPaymentMethods'],
    }),

    setDefaultPaymentMethod: builder.mutation({
      query: (id) => ({
        url: `/payment-methods/${id}/set-default`,
        method: 'PUT',
      }),
      invalidatesTags: ['SavedPaymentMethods'],
    }),

    getReturnLabelQuote: builder.query({
      query: (orderItemId) => ({
        url: `/returns/${orderItemId}/label-quote`,
        method: 'GET',
      }),
    }),

    processReturnLabelPayment: builder.mutation({
      query: ({ orderItemId, ...data }) => ({
        url: `/returns/${orderItemId}/process-label-payment`,
        method: 'POST',
        body: data,
      }),
    }),

    generateReturnLabel: builder.mutation({
      query: ({ orderItemId, fulfillmentGroupId, fromAddressId, toAddressId, rateId, paymentMethod, amount, currency, isReturn = false, parcels }) => ({
        // Redirect to the delivery endpoint instead of the payment endpoint
        url: `/delivery/create-return-shipping-label`,
        method: 'POST',
        body: { 
          fulfillmentGroupId,
          orderItemId,
          fromAddressId, 
          toAddressId, 
          rateId, 
          paymentMethod, 
          amount, 
          currency,
          isReturn,
          parcels
        }
      }),
      invalidatesTags: (result) => result?.success ? 
        [
          { type: 'OrderItem', id: 'LIST' },
          { type: 'FulfillmentGroup', id: 'LIST' },
          { type: 'Request', id: 'LIST' }
        ] : []
    }),

    // Merchant fund collection endpoints
    createFundCollectionIntent: builder.mutation({
      query: (data) => ({
        url: '/merchant/create-fund-collection-intent',
        method: 'POST',
        body: {
          amount: data.amount,
          currency: data.currency,
          fulfillmentGroupId: data.fulfillmentGroupId,
          description: data.description
        }
      }),
      transformResponse: (response) => {
        console.log('Create fund collection intent response:', response);
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('Error creating fund collection intent:', response);
        return response;
      }
    }),

    confirmFundCollection: builder.mutation({
      query: (data) => ({
        url: '/merchant/confirm-fund-collection',
        method: 'POST',
        body: {
          paymentIntentId: data.paymentIntentId,
          fulfillmentGroupId: data.fulfillmentGroupId,
          usageFee: data.usageFee
        }
      }),
      transformResponse: (response) => {
        console.log('Confirm fund collection response:', response);
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('Error confirming fund collection:', response);
        return response;
      },
      invalidatesTags: ['AccountBalance', 'MerchantFundCollectionPayments'] // Refresh balance and fund collection payments after fund collection
    }),

    getMerchantFundCollectionPayments: builder.query({
      query: () => ({
        url: '/merchant/fund-collection-payments',
        method: 'GET'
      }),
      transformResponse: (response) => {
        console.log('Get merchant fund collection payments response:', response);
        return response;
      },
      transformErrorResponse: (response) => {
        console.error('Error fetching merchant fund collection payments:', response);
        return response;
      },
      providesTags: ['MerchantFundCollectionPayments']
    }),
  }),
});

export const {
  useCreateStripeAccountMutation,
  useCreateAccountSessionMutation,
  useGetAccountStatusQuery,
  useGetAccountBalanceQuery,
  useCreatePaymentIntentMutation,
  useConfirmPaymentMutation,
  useFetchPaymentMethodsQuery,
  useRefundPaymentMutation,
  useCreateMultiMerchantPaymentIntentMutation,
  useGetPaymentsByOrderIdQuery,
  useUpdateOrderStatusMutation,
  useGetPlatformPendingProductEarningsQuery,
  useGetSavedPaymentMethodsQuery,
  useCreateSetupIntentMutation,
  useSavePaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useGetReturnLabelQuoteQuery,
  useProcessReturnLabelPaymentMutation,
  useGenerateReturnLabelMutation,
  useCreateFundCollectionIntentMutation,
  useConfirmFundCollectionMutation,
  useGetMerchantFundCollectionPaymentsQuery,
} = paymentApi;

// Add the new prepareCheckout mutation separately to avoid merge conflicts if file is large
// This is a common pattern if the builder callback gets too extensive.
// However, for typical RTK Query, you'd add it within the same builder.endpoints callback.
// For simplicity here, let's assume it's added to the existing builder.

export const paymentApiWithPrepareCheckout = paymentApi.injectEndpoints({
  endpoints: (builder) => ({
    prepareCheckout: builder.mutation({
      query: (payload) => ({
        url: '/payments/prepare-checkout',
        method: 'POST',
        body: payload, // { shippableItems, shippingAddressId, selectedShippingRatesDetails, targetCurrency }
      }),
      // We might want to invalidate or update something upon success/failure if needed
      // For now, it primarily returns clientSecret and strapiPaymentId
    }),
  }),
  overrideExisting: false, // Important if injecting into an existing API slice
});

export const {
  usePrepareCheckoutMutation,
  // Re-export existing hooks if you are creating a new enhanced api instance.
  // If modifying directly, this section might not change much.
} = paymentApiWithPrepareCheckout;

// Add a new mutation for removing purchased items from cart
export const paymentApiWithRemovePurchased = paymentApi.injectEndpoints({
  endpoints: (builder) => ({
    removePurchasedItemsFromCart: builder.mutation({
      query: (payload) => ({ // payload: { purchasedItems, currency }
        url: '/carts/remove-purchased',
        method: 'POST',
        body: payload,
      }),
      // Optionally, invalidate cart tags if you have them in cartApi
      // invalidatesTags: [{ type: 'Cart', id: 'ME' }], 
    }),
  }),
  overrideExisting: false,
});

export const {
  useRemovePurchasedItemsFromCartMutation,
} = paymentApiWithRemovePurchased;
